/* eslint-disable functional/prefer-readonly-type */
import * as Ant from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import React from "react";

import * as Color from "~/Color";
import { DVIR, DVIRs } from "~/DVIR";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import { Order } from "~/Order";
import { Time } from "~/Time";

export function Status({ dvir }: { readonly dvir: DVIR }) {
  return pipe(
    DVIR.isNoDefects(dvir)
      ? [<Icon.CheckCircle color={Color.gray()} />, "No defects"]
      : DVIR.isResolved(dvir)
      ? [<Icon.CheckCircle color={Color.green()} />, "Resolved"]
      : [<Icon.TimesCircle color={Color.guardsmanRed()} />, "Unresolved"],
    ([icon, status]) => (
      <>
        <Layout.Inline.Component right>{icon}</Layout.Inline.Component>
        {status}
      </>
    )
  );
}

export namespace Status {
  export enum StatusEnum {
    RESOLVED,
    UNRESOLVED,
    NODEFECT,
  }

  export const column = ({
    status = [],
    onChange,
  }: {
    readonly status?: StatusEnum[];
    readonly onChange: (status?: CheckboxValueType[]) => void;
  }) => ({
    title: "Status",
    dataIndex: "status" as const,

    filteredValue: status?.length ? ["status"] : undefined,
    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter {...{ status, onChange }} {...props} />
    ),
    onFilter: (_: unknown, dvir: DVIR) => pipe(dvir, getFilterFunc(status)),
    render: (_: unknown, dvir: DVIR) => <Status dvir={dvir} />,
    sorter: (a: DVIR, b: DVIR): Order.Ordering =>
      pipe(
        Order.number().compare(
          DVIR.isResolved(a) ? 1 : 0,
          DVIR.isResolved(b) ? 1 : 0
        ),
        (order) =>
          order !== Order.equal()
            ? order
            : Time.DateTime.compare(
                a.lifecycle.submitted,
                b.lifecycle.submitted
              )
      ),
  });

  export const getFilterFunc = (status?: readonly StatusEnum[]) => (dvir: DVIR) =>
    status === undefined ||
    status.length === 0 ||
    (status.includes(StatusEnum.NODEFECT) && DVIR.isNoDefects(dvir)) ||
    (status.includes(StatusEnum.RESOLVED) && DVIR.isResolved(dvir)) ||
    (status.includes(StatusEnum.UNRESOLVED) && DVIR.isUnresolved(dvir));

  export const getFilterFuncForSettings = (settings: DVIRs.Table.Settings) =>
    getFilterFunc(settings.status);

  export const Filter = ({
    status,
    onChange,
    confirm,
  }: DVIRs.Table.Filter.Props & Parameters<typeof column>[0]) => (
    <DVIRs.Table.Filter
      confirm={confirm}
      onReset={status === undefined ? status : onChange}
    >
      <Ant.Checkbox.Group
        onChange={(values) => onChange(values)}
        value={status}
      >
        <Ant.Space direction="vertical">
          <Ant.Checkbox value={StatusEnum.RESOLVED}>Resolved</Ant.Checkbox>
          <Ant.Checkbox value={StatusEnum.UNRESOLVED}>Unresolved</Ant.Checkbox>
          <Ant.Checkbox value={StatusEnum.NODEFECT}>No Defects</Ant.Checkbox>
        </Ant.Space>
      </Ant.Checkbox.Group>
    </DVIRs.Table.Filter>
  );
}
